import React, { useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorageIcon from "@mui/icons-material/Storage";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  EditableService,
  EnvKey,
  EnvServiceValues,
} from "./editable-services-constants";
import {
  CPU_TIERS,
  CPU_TIERS_STATEFUL,
  MEMORY_TIERS,
  MEMORY_TIERS_STATEFUL,
  STORAGE_TIERS,
  cpuLabel,
  formatCpu,
  formatMemory,
  formatStorage,
  memoryLabel,
  parseCpu,
  parseMemory,
  storageLabel,
} from "./k8sResources";
import { ResourceTierButtons } from "./ResourceTierButtons";

// ── Service Row (per-card, rendered for active env) ──────────────────────────

export interface ServiceRowProps {
  service: EditableService;
  activeEnv: EnvKey;
  editable: boolean;
  isBaseEnv: boolean;
  onRemove: () => void;
  onChange: (env: EnvKey, updates: Partial<EnvServiceValues>) => void;
}

export function ServiceRow({
  service,
  activeEnv,
  editable,
  isBaseEnv,
  onRemove,
  onChange,
}: ServiceRowProps) {
  const [expanded, setExpanded] = useState(false);
  const envValues = service.environments[activeEnv];
  const isStateful = service.type === "stateful";
  const isStatic = service.type === "static";

  const cpuK8s = formatCpu(envValues.cpu);
  const memoryK8s = formatMemory(envValues.memory * 1024);
  const storageK8s = formatStorage(envValues.storage);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <StorageIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="subtitle2" fontWeight={600}>
          {service.name}
        </Typography>
        <Chip
          label={service.type}
          size="small"
          color="primary"
          variant="outlined"
        />
        {service.internetFacing && (
          <Chip
            label="Public"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {!isStatic && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {cpuK8s} CPU / {memoryK8s} Mem
            {isStateful ? ` / ${storageK8s} Storage` : ""} &middot;{" "}
            {envValues.replicas}{" "}
            {envValues.replicas === 1 ? "replica" : "replicas"}
          </Typography>
        )}
        {isStatic && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            CDN deployment
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {editable && (
          <Tooltip title={`Remove ${service.name} from this stack`}>
            <IconButton size="small" color="error" onClick={onRemove}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {isStatic ? (
          <Alert severity="info" variant="outlined" sx={{ mt: 2, mb: 0 }}>
            Static services are deployed to a CDN — CPU, Memory, and Replicas
            are not applicable. Build and deploy are handled by the static
            hosting pipeline.
          </Alert>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <ResourceTierButtons
                label="CPU"
                value={cpuK8s}
                onChange={(v) => onChange(activeEnv, { cpu: parseCpu(v) })}
                tiers={isStateful ? CPU_TIERS_STATEFUL : CPU_TIERS}
                customSuffix="m"
                disabled={!editable}
                helperText={cpuLabel(envValues.cpu)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <ResourceTierButtons
                label="Memory"
                value={memoryK8s}
                onChange={(v) =>
                  onChange(activeEnv, { memory: parseMemory(v) / 1024 })
                }
                tiers={isStateful ? MEMORY_TIERS_STATEFUL : MEMORY_TIERS}
                customSuffix="Mi"
                disabled={!editable}
                helperText={memoryLabel(envValues.memory * 1024)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              {isStateful ? (
                <ResourceTierButtons
                  label="Storage"
                  value={storageK8s}
                  onChange={(v) =>
                    onChange(activeEnv, { storage: parseInt(v) || 0 })
                  }
                  tiers={STORAGE_TIERS}
                  customSuffix="Gi"
                  disabled={!editable}
                  helperText={storageLabel(envValues.storage)}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Storage: N/A (non-stateful)
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Replicas"
                type="number"
                value={envValues.replicas}
                onChange={(e) =>
                  onChange(activeEnv, {
                    replicas: Math.max(
                      1,
                      Math.min(10, parseInt(e.target.value) || 1),
                    ),
                  })
                }
                disabled={!editable}
                inputProps={{ min: 1, max: 10 }}
                sx={{ width: 120 }}
                helperText="1-10 pods"
                size="small"
              />
            </Grid>
          </Grid>
        )}

        {!isBaseEnv && !isStatic && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Changes override the dev base values for this environment.
          </Typography>
        )}
      </Collapse>
    </Paper>
  );
}

export default ServiceRow;
